import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Row, Skeleton, Space, Tag, Typography } from 'antd';
import { PrinterOutlined, SendOutlined } from '@ant-design/icons';
import type { BriefingSummary } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Screen 13. Morning briefing.
 * Real KPIs derived from the setup + students + staff + fees tables. Values
 * that read from not-yet-built modules (today's attendance %, collected today)
 * render an honest "waiting for module" note rather than a fabricated number.
 */
export function BriefingPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const q = useQuery({
    queryKey: ['briefing', institution],
    queryFn: () => productApi.getBriefing(institution),
    enabled: !!institution,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {today}
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data
              ? q.data.setup.complete
                ? `Setup complete · ${q.data.students.total} students · ${q.data.staff.active} active staff`
                : `Setup ${q.data.setup.steps_done} of ${q.data.setup.steps_total} · finish setup to open the daily loop`
              : 'Loading…'}
          </Typography.Text>
        </div>
        <Space size={8}>
          <Button icon={<PrinterOutlined />} disabled title="Print output ships with U-15 pass">
            Print day sheet
          </Button>
          <Button type="primary" icon={<SendOutlined />} disabled title="Broadcasts ship with the Comms module">
            Send a broadcast
          </Button>
        </Space>
      </div>

      {q.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
      {q.isError && <Alert type="error" showIcon message="Could not load briefing" description={String(q.error)} />}
      {q.data && <Body data={q.data} institution={institution} />}
    </Space>
  );
}

function Body({ data, institution }: { data: BriefingSummary; institution: string }) {
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Kpi
            label="Attendance today"
            value={data.attendance.today_percent !== null ? `${data.attendance.today_percent}%` : '—'}
            foot={
              data.attendance.today_percent !== null
                ? `${fmt(data.students.enrolled)} enrolled · ${data.attendance.mode ?? 'daily'} mode`
                : 'no registers marked today yet'
            }
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Kpi
            label="Collected today"
            value={
              data.fees.collected_today_paise !== null
                ? `₹${(data.fees.collected_today_paise / 100).toLocaleString('en-IN')}`
                : '—'
            }
            foot={data.fees.collected_today_paise ? 'from receipts written today' : 'no receipts today'}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Kpi
            label="Students"
            value={fmt(data.students.total)}
            foot={`${data.students.enrolled} enrolled · ${data.students.with_primary_guardian} with primary guardian`}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Kpi
            label="Staff"
            value={fmt(data.staff.active)}
            foot={`of ${data.staff.total} on roster`}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} md={18}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="Setup" extra={data.setup.complete ? <Tag color="success">complete</Tag> : <Tag color="warning">{data.setup.steps_done} / {data.setup.steps_total}</Tag>}>
            {data.setup.complete ? (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                All six setup steps are done. The daily loop screens are ready to fill in as their modules ship.
              </Typography.Text>
            ) : (
              <div>
                <Typography.Paragraph style={{ margin: 0, fontSize: 13 }}>
                  {data.setup.steps_total - data.setup.steps_done} step
                  {data.setup.steps_total - data.setup.steps_done === 1 ? '' : 's'} left.{' '}
                  <a href={`/i/${institution}/setup`}>Open setup</a> to continue.
                </Typography.Paragraph>
              </div>
            )}
          </Card>

          <Card size="small" title="Waiting on modules" extra={<Tag color="purple">honest</Tag>}>
            <ModuleRow name="Staff absent today" needs="HR biometric attendance feed" />
            <ModuleRow name="Uncovered periods" needs="Timetable + substitutions" />
            <ModuleRow name="Admissions ready for interview" needs="Admissions module" />
            <ModuleRow name="Guardian broadcast delivery" needs="Comms module (SMS + WhatsApp)" last />
          </Card>
          </Space>
        </Col>

        <Col xs={24} md={6}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="Calendar" extra={data.calendar.current_session_label ? <Tag color="success">{data.calendar.current_session_label}</Tag> : <Tag>no session</Tag>}>
            {data.calendar.current_session_label ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <MiniStat label="Terms" value={data.calendar.terms_in_current_session} />
                <MiniStat label="Holidays" value={data.calendar.holidays_in_current_session} />
              </div>
            ) : (
              <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5 }}>
                No current academic session. <a href={`/i/${institution}/setup/calendar`}>Set one</a>.
              </Typography.Paragraph>
            )}
          </Card>

          <Card size="small" title="Structure">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <MiniStat label="Grades" value={data.structure.grades} />
              <MiniStat label="Sections" value={data.structure.sections} />
            </div>
            {data.structure.grades === 0 && (
              <Typography.Paragraph type="secondary" style={{ margin: '10px 0 0', fontSize: 12 }}>
                Optional — <a href={`/i/${institution}/admin/structure/grades`}>define grades</a> to
                unlock structure-aware dropdowns on student create.
              </Typography.Paragraph>
            )}
          </Card>

          <Card size="small" title="Attendance mode">
            {data.attendance.mode ? (
              <Space size={8}>
                <Tag color="processing">{data.attendance.mode}</Tag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {data.attendance.mode === 'daily'
                    ? 'One register per day per class.'
                    : 'One register per period per class.'}
                </Typography.Text>
              </Space>
            ) : (
              <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5 }}>
                Not set yet. <a href={`/i/${institution}/setup/attendance-mode`}>Choose one</a>.
              </Typography.Paragraph>
            )}
          </Card>
          </Space>
        </Col>
      </Row>
    </Space>
  );
}

function Kpi({ label, value, foot }: { label: string; value: string; foot: string }) {
  return (
    <Card size="small">
      <div style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', color: '#8a94a6', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: '#14213d', marginTop: 4 }}>
        {value}
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>{foot}</Typography.Text>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', color: '#8a94a6', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: '#14213d', marginTop: 2 }}>
        {fmt(value)}
      </div>
    </div>
  );
}

function ModuleRow({ name, needs, last }: { name: string; needs: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 10,
        padding: '8px 4px',
        borderBottom: last ? 'none' : '1px solid #f1f1f4',
        alignItems: 'center',
      }}
    >
      <div>
        <div style={{ fontSize: 13, color: '#14213d' }}>{name}</div>
        <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>{needs}</Typography.Text>
      </div>
      <Tag color="default" style={{ margin: 0 }}>waiting</Tag>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString('en-IN');
}
